/**
 * 多版本菜单 B6（老扫码站 U-B6-3）：菜单预览码 —— 「这是不是预览、码是什么」全站只问这里。
 *
 * 管理端预览链接是 `/v/<venueId>/p?previewMenu=<32 位小写十六进制>`（管理端 `src/features/menu-versions/preview.ts:35-38`）。
 * 带着这个码的页面看的是那份还没上线的菜单（后端 `MenuPreviewTokenService` 凭码强制那份菜单），**不能下真单**。
 *
 * ## 零存储（owner D80 / D88 / D98②）
 * 码**只从当前地址现读**：`window.location.search`，以及导航进行中还没写进地址栏的那一下用路由 query 兜着。
 * 绝不写进 Vuex（根 state 整份落 `localStorage.vuex`）、localStorage、sessionStorage、cookie、菜单缓存键或别的任何地方 ——
 * 后端对形状合法的 `X-Preview-Menu` 头**一律拒单**（D80，不看链接还在不在分享），码一旦被存下来，那台手机就不限期下不了单。
 * 也不要照抄 `?ab_force` 写 sessionStorage 的做法（`utils/abForce.js`）。
 * 站内跳转靠路由守卫把上一页地址里的码补到下一页（`carryPreviewMenu`，`router/index.js`）——读的是上一页的地址，不是存储。
 * 推论：**站内导航退不出预览**（与 kiosk 相同）；退出只能整页打开一个不带码的地址。
 *
 * ## 判据（与 kiosk `src/shared/review/previewMenu.ts` 的下单头判据一致）
 * 地址里 previewMenu 的**任何一个值**形状合法 ⇒ 第一个合法值；否则没有预览。老站没有 review 模式（owner B6-Q6 = A：
 * 扫码端只认码，不要求 `preview=1`）。读菜单、带请求头、本地拦单、横幅都用这一条。
 *
 * ## 统计（owner D93）另一条判据
 * 地址里**有 previewMenu 这个键**（不看值对不对：坏码的链接也是管理端发出来的）就不注入 Umami、不装 vue-gtag、不启动 OpenReplay、
 * 不启动性能监控（U-B6-2 review 第 2 轮 G10：它把整个地址放进上报数据）。
 * 这个判断只在应用启动时按 `window.location` 做一次（`utils/umami.js`、`utils/openreplay.js`、`main.js`、`utils/performance.js`）：
 * 没有哪条站内路径能在不整页打开的情况下**进入**预览 —— 守卫只会把码从上一页带过来，上一页本来就是预览页。
 * 将来要是加了「站内进入预览」的入口，必须同时处理已经加载好的统计脚本，否则码会被它们记下来。
 *
 * 本文件是纯函数（只读 `window.location`），不 import 任何别的模块：`utils/umami.js` / `utils/openreplay.js` 在模块加载时就用它。
 */

export const PREVIEW_MENU_QUERY_KEY = 'previewMenu'

/** 后端 `MenuPreviewTokenService::HEADER`（已进 CORS 白名单，`ApiCommonController.php:112`） */
export const PREVIEW_MENU_HEADER = 'X-Preview-Menu'

/**
 * 后端签发的形状：`bin2hex(random_bytes(16))`，后端判形状用 `MenuPreviewTokenService::TOKEN_PATTERN` = `/^[0-9a-f]{32}$/D`。
 * JS 的 `$` 不带 m 标志时本来就是严格结尾，与 PHP 加 D 等价。改形状两侧一起改（测试里有手抄镜像）。
 */
export const PREVIEW_MENU_TOKEN_PATTERN = /^[0-9a-f]{32}$/

/** 后端带着预览码下单被拒时 `error.code` 的值（`OrderingGate::PREVIEW_REJECT_CODE`） */
export const PREVIEW_ORDERING_CODE = 'menuPreviewOrdering'

/** 一个值是不是合法预览码：是 → 原样返回；数组 / 非字符串 / 形状不对 → null */
export function normalizePreviewMenuToken(v) {
  return typeof v === 'string' && PREVIEW_MENU_TOKEN_PATTERN.test(v) ? v : null
}

/** 一个 query 值（重复参数时 vue-router 给数组）里第一个形状合法的码，没有 → null */
export function firstPreviewMenuToken(v) {
  const values = Array.isArray(v) ? v : [v]
  for (let i = 0; i < values.length; i++) {
    const token = normalizePreviewMenuToken(values[i])
    if (token !== null) return token
  }
  return null
}

/** 从 query string（如 `location.search`）取码：任何一个值合法就取第一个合法的 */
export function previewMenuTokenFromSearch(search) {
  if (typeof search !== 'string') return null
  try {
    return firstPreviewMenuToken(new URLSearchParams(search).getAll(PREVIEW_MENU_QUERY_KEY))
  } catch (e) {
    return null
  }
}

/** query string 里有没有 previewMenu 这个键（不看值）。统计闸用 */
export function searchHasPreviewMenuKey(search) {
  if (typeof search !== 'string') return false
  try {
    return new URLSearchParams(search).has(PREVIEW_MENU_QUERY_KEY)
  } catch (e) {
    return false
  }
}

/** 这个地址能不能统计（D93）：没有 previewMenu 键才能 */
export function analyticsAllowedAt(search) {
  return !searchHasPreviewMenuKey(search)
}

/** query string 里一段 `key=value` 的键（解码；解不了就原样） */
function queryPartKey(part) {
  const raw = part.split('=')[0].replace(/\+/g, ' ')
  try {
    return decodeURIComponent(raw)
  } catch (e) {
    return raw
  }
}

/**
 * 把一个完整地址里的 previewMenu 参数（不管几个、值对不对）去掉，别的参数、路径、hash 逐字节不动；没有这个键 ⇒ 原样还回去。
 * 要送出这台设备的地址（提交 bug 报告的 `details.url` 等）用（U-B6-2 review 第 2 轮 G10，与 D93 的统计闸同一类）：码只该在地址栏和预览请求头里。
 */
export function withoutPreviewMenu(href) {
  if (typeof href !== 'string') return href
  const hashAt = href.indexOf('#')
  const beforeHash = hashAt < 0 ? href : href.slice(0, hashAt)
  const hash = hashAt < 0 ? '' : href.slice(hashAt)
  const queryAt = beforeHash.indexOf('?')
  if (queryAt < 0) return href
  const parts = beforeHash.slice(queryAt + 1).split('&')
  const kept = parts.filter(part => queryPartKey(part) !== PREVIEW_MENU_QUERY_KEY)
  if (kept.length === parts.length) return href
  const query = kept.join('&')
  return beforeHash.slice(0, queryAt) + (query ? `?${query}` : '') + hash
}

/**
 * 当前页面的预览码，没有 → null。每次现读：先看地址栏，再看调用方给的路由 query
 * （导航进行中、history 还没写的那一下，或调用方手上只有 `$route`）。两处都只是地址，不是存储。
 * @param {Object} [routeQuery] `router.currentRoute.query` / `this.$route.query`
 */
export function currentPreviewMenuToken(routeQuery) {
  const search = typeof window !== 'undefined' && window.location ? window.location.search : ''
  const fromAddress = previewMenuTokenFromSearch(search)
  if (fromAddress !== null) return fromAddress
  return routeQuery && typeof routeQuery === 'object' ? firstPreviewMenuToken(routeQuery[PREVIEW_MENU_QUERY_KEY]) : null
}

/**
 * 路由守卫用：上一页地址里有合法码、下一页没有**单个合法码**（没带 / 空串 / 坏码 / 重复键）⇒ 返回补上码的下一页地址
 * （`next(location)` 用；别的参数与 hash 原样），否则 null（放行）。补过之后再判一次一定是 null，不会死循环。
 * 下一页自己带着合法码时不动。
 */
export function carryPreviewMenu(to, from) {
  const token = firstPreviewMenuToken(from && from.query ? from.query[PREVIEW_MENU_QUERY_KEY] : undefined)
  if (token === null) return null
  const query = (to && to.query) || {}
  if (normalizePreviewMenuToken(query[PREVIEW_MENU_QUERY_KEY]) !== null) return null
  return { path: to.path, query: { ...query, [PREVIEW_MENU_QUERY_KEY]: token }, hash: to.hash || '' }
}
